/**
 * Fixed, venue-independent palette for the Feather Reel.
 *
 * Ported 1:1 from iOS `ReelTheme.swift` / `ReelBackgroundTheme.swift` (which mirror Android). The
 * reel is a full-screen dark storytelling surface whose look must be identical across every venue,
 * so these are hard-coded hex — deliberately NOT the per-venue `--menu-*` variables.
 */

/** Page + persistent-panel background. */
export const REEL_BG = '#141414';
/** Accent: CTAs, price pill, the waiter bell. */
export const REEL_CORAL = '#F26B6B';
/** Ask-panel inner card vertical gradient (top → bottom). */
export const REEL_CARD_TOP = '#1E1E1E';
export const REEL_CARD_BOTTOM = '#191919';
export const REEL_CARD_BORDER = '#2B2B2B';
/** Hairline above the persistent panel. */
export const REEL_PANEL_DIVIDER = '#202020';
/** Muted subtitle grey inside the panel. */
export const REEL_SUB_GREY = '#9A9A9A';

/**
 * One end-card gradient theme. `(top, mid, bottom)` drive the radial background; `scrim` is the
 * darkest shade used for the bottom text scrim. Tuples mirror Android `ReelBackgroundTheme` exactly.
 */
export interface ReelBackgroundTheme {
  top: string;
  mid: string;
  bottom: string;
  scrim: string;
}

/** The six end-card themes keyed by uppercased `backgroundType`. */
const REEL_BACKGROUND_THEMES: Record<string, ReelBackgroundTheme> = {
  RED: { top: '#F26B6B', mid: '#D1413F', bottom: '#6F1A19', scrim: '#5A1211' },
  BLUE: { top: '#5B8DEF', mid: '#2E5BC0', bottom: '#16264F', scrim: '#10203F' },
  TEAL: { top: '#2FBFA0', mid: '#1E8A73', bottom: '#0C352C', scrim: '#0C2E26' },
  PURPLE: { top: '#9B6BE0', mid: '#6A3FB0', bottom: '#2B1850', scrim: '#241540' },
  AMBER: { top: '#F0A93B', mid: '#D67A1E', bottom: '#6E3A0C', scrim: '#5C3009' },
  BLACK: { top: '#2A2A2A', mid: '#1C1C1C', bottom: '#121212', scrim: '#0E0E0E' },
};

/**
 * Select an end-card theme by `backgroundType` (trimmed, case-insensitive). Unknown/blank → RED.
 * Mirrors iOS `ReelBackgroundTheme.from`.
 */
export function getReelBackgroundTheme(
  backgroundType: string | null | undefined
): ReelBackgroundTheme {
  const key = (backgroundType ?? '').trim().toUpperCase();
  return REEL_BACKGROUND_THEMES[key] ?? REEL_BACKGROUND_THEMES['RED'];
}

/**
 * The end-card radial background as a CSS `radial-gradient(...)` string.
 *
 * iOS: `top@0 → mid@0.42 → bottom@1`, centered at (0.5, 0.08), radius = width × 1.2. CSS can't
 * express "120% of own width" as a circle radius, so the caller passes the card's measured pixel
 * width; radius = `widthPx * 1.2`. When width is unknown (0), fall back to `farthest-side`, which
 * spreads to the bottom edge — a close visual match.
 */
export function reelBackgroundGradient(theme: ReelBackgroundTheme, widthPx = 0): string {
  const size = widthPx > 0 ? `${Math.round(widthPx * 1.2)}px` : 'farthest-side';
  return `radial-gradient(circle ${size} at 50% 8%, ${theme.top} 0%, ${theme.mid} 42%, ${theme.bottom} 100%)`;
}
